import { Select, Spinner } from "@rio-cloud/rio-uikit";
import Checkbox from "@rio-cloud/rio-uikit/Checkbox";
import Dialog from "@rio-cloud/rio-uikit/Dialog";
import { useNavigate } from "react-router-dom";

import { Asset, AssetModel, CampaignsDetails } from "../../../../../../models/asset";
import { CheckupService } from "../../../../../../models/checkup";

import { useConsultantSchedulingFormAssetDriverAndDate } from "../../../../../../hooks/useConsultantSchedulingForm";
import { IAppointmentForm } from "../../../../../../hooks/useSchedulingForm";
import { useIntl } from "react-intl";

import "../styles.scss";
import SearchAutocomplete from "../../../../../../components/form/searchAutocomplete";
import useAssetModels from "../../../../../../hooks/useAssetModels";
import { useDebouncedCallback } from "../../../../../../hooks/useDebounce";
import VehicleIcon from "../../../../../../components/VehicleIcon";
import ActiveAppointmentDialog from "../../../../../../components/dialogs/ActiveAppointmentDialog";
import useDarkMode from "../../../../../../hooks/useDarkMode";

interface IAppointmentFormProps {
  goToSummary: () => void;
  form: IAppointmentForm | undefined;
  setForm: (form: IAppointmentForm) => void;
  assets: Asset[];
  appointmentId?: string;
}

const AppointmentForm = ({
  goToSummary,
  form,
  setForm,
  assets,
  appointmentId,
}: IAppointmentFormProps) => {
  const navigate = useNavigate();

  const {
    assetId,
    firstWeekDay,
    weekDays,
    consultantScheduleDates,
    choosedDate,
    setChoosedDate,
    showDialog,
    setShowDialog,
    loading,
    setLoading,
    vehicleLoading,
    vehicleError,
    setVehicleError,
    disableAssetGroup,
    activeAppointmentDialog,
    setActiveAppointmentDialog,
    previousWeek,
    nextWeek,
    selectDate,
    handleClose,
    chooseAsset,
    getCheckupByMetric,
    setCheckup,
    formIsValid,
    getConsultantAvailability,
  } = useConsultantSchedulingFormAssetDriverAndDate(assets, form!, setForm);

  const { assetModels } = useAssetModels();
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id: `intl-msg:appointment.consultantForm.${id}` });
  const isDarkMode = useDarkMode();

  // Callbacks debounced para odômetro e horímetro (3000ms de delay)
  const debouncedOdometerCheckup = useDebouncedCallback(
    async (value: number, asset: any) => {
      if (value && asset) {
        await setCheckup(value, "odometer", asset);
      }
    },
    3000
  );

  const debouncedHourmeterCheckup = useDebouncedCallback(
    async (value: number, asset: any) => {
      if (value && asset) {
        await setCheckup(value, "hourmeter", asset);
      }
    },
    3000
  );

  return (
    <>
      {/* Loading Modal para busca de veículo */}
      {vehicleLoading && (
        <div className="fixed inset-0 bg-gray-500/75 dark:bg-black/75 flex items-center justify-center z-[9999]">
          <div className="bg-white dark:bg-neutral-800 rounded-lg p-8 flex flex-col items-center shadow-lg">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-700 dark:text-neutral-300 font-medium">{t('loadingVehicle')}</p>
          </div>
        </div>
      )}

      <div className="schedule-consultant">
        <div className="schedule-consultant overflow-auto bg-white dark:bg-neutral-900 flex flex-wrap">
          <div className="w-full lg:w-2/3 md:w-full">
            <div className="schedule-consultant__header">
              <h2 className="text-color-black font-semibold">
                {t('title')}
              </h2>
              <p className="text-color-status-resting text-size-16">
                {t('subtitle')}
              </p>
            </div>

            <div className="border rounded padding-15 margin-top-20 scheduling-page__fieldset">
              <h6 className="text-bold text-size-16">{t('vehicleInfo')}</h6>

              {!assetId && (
                <div className="flex justify-between w-full gap-6 mt-5">
                  <div className="form-group form-group-lg w-full">
                    <label htmlFor="placa">
                      {t('searchVehicleLabel')}<span className="asterisk-color">*</span>
                    </label>
                    <Select
                      placeholder={t('searchVehiclePlaceholder')}
                      options={assets
                        .filter((asset: Asset) => asset.chassis !== "-")
                        .map((asset: Asset) => {
                          const licensePlateText = asset.licensePlate ? ` - ${asset.licensePlate}` : '';
                          return {
                            id: asset.id,
                            label: `${asset.name} - ${asset.chassis}${licensePlateText}`,
                            chassis: asset.chassis,
                          };
                        })}
                      value={
                        form?.assetsAndDrivers?.asset
                          ? [form?.assetsAndDrivers?.asset.id]
                          : []
                      }
                      useFilter
                      noItemMessage={t('noVehicleFound')}
                      onChange={(e) => {
                        setVehicleError(''); // Limpa erro anterior
                        chooseAsset(
                          e?.id ?? "",
                          e?.chassis ?? "",
                          e?.label ?? ""
                        );
                      }}
                      inputAddon="rioglyph rioglyph-search"
                      disabled={!!appointmentId}
                    />
                    
                    {/* Mensagem de erro para veículos com agendamento existente */}
                    {vehicleError && (
                      <div className="mt-3 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-900 rounded-md">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <svg className="h-6 w-6 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="ml-3">
                        <div className="text-red-700 dark:text-red-300 font-medium" style={{ fontSize: '16px' }}>
                          <p style={{ whiteSpace: 'pre-line' }}>{vehicleError}</p>
                        </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {form?.assetsAndDrivers?.asset && assetId && (
                <div className="border rounded padding-15 margin-top-20 margin-bottom-20 scheduling-page__fieldset">
                  <div className="flex items-center gap-4">
                    <VehicleIcon 
                      chassis={form?.assetsAndDrivers?.asset.chassis} 
                      size={80}
                    />

                    <div className="flex-1 padding-left-10">
                      <p className="text-color-black text-size-16 font-medium m-0">
                        {form?.assetsAndDrivers?.asset.name}
                      </p>
                      <p className="text-color-status-resting text-size-14 m-0">
                        {t('chassisLabel')} {form?.assetsAndDrivers?.asset.chassis}
                      </p>
                    </div>

                    {(form?.assetsAndDrivers?.asset?.plans?.length ?? 0) >
                      0 && (
                        <div className="flex gap-6 margin-bottom-20">
                          <span className="px-2 py-1 green-tag">RIO</span>
                          <span className="px-2 py-1 green-tag">{t('inWarranty')}</span>
                        </div>
                      )}
                  </div>
                </div>
              )}

              <div className="w-full grid grid-cols-12 gap-6">
                <div className="col-span-12 md:col-span-6 lg:col-span-4 form-group form-group-lg">
                  <label htmlFor="placa">{t('plateLabel')}</label>
                  <input
                    id="placa"
                    className="form-control"
                    type="text"
                    placeholder={t('platePlaceholder')}
                    value={form?.assetsAndDrivers?.asset?.licensePlate || ""}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        assetsAndDrivers: {
                          ...form?.assetsAndDrivers,
                          asset: {
                            ...form?.assetsAndDrivers?.asset!,
                            licensePlate: e.target.value,
                          }
                        }
                      })
                    }
                    disabled={!!form?.assetsAndDrivers?.asset?.licensePlateFromEP}
                    maxLength={7}
                  />
                </div>

                <div className="col-span-12 md:col-span-6 lg:col-span-4 form-group form-group-lg">
                  <label htmlFor="model">{t('modelLabel')}</label>
                  {form?.assetsAndDrivers?.asset?.model && form?.assetsAndDrivers?.asset?.model !== "-" ? (
                    <input
                      id="model"
                      className="form-control"
                      type="text"
                      value={form?.assetsAndDrivers?.asset?.model}
                      disabled
                    />
                  ) : (
                    <SearchAutocomplete
                      id="model"
                      placeholder={t('modelPlaceholder')}
                      fetchOptions={async (searchTerm) => assetModels
                        .filter((model) => model.modelId && model.description && model.description.toLowerCase().includes(searchTerm?.toLowerCase()))
                      }
                      optionLabel={{
                        modelCode: t('modelCode'),
                        description: t('modelDescription'),
                      }}
                      optionRenderer={(model: AssetModel) => (
                        <div>
                          <strong>{t('modelCode')}:</strong> {model.modelCode} - <strong>{t('modelDescription')}:</strong> {model.description ?? "-"}
                        </div>
                      )}
                      onSelectedOption={(model: AssetModel) => {
                        setForm({
                          ...form,
                          assetsAndDrivers: {
                            ...form?.assetsAndDrivers,
                            asset: form?.assetsAndDrivers?.asset ? { ...form?.assetsAndDrivers?.asset, model: model.description, modelCode: model.modelCode, vehicleModel: model.modelId } : undefined,
                          },
                        });
                      }}
                      overlay
                      overlayClassName="!w-[44%]"
                      defaultValue={form?.assetsAndDrivers?.asset?.model && assetModels.find((model) => model.modelId === form?.assetsAndDrivers?.asset?.model)}
                      disabled={disableAssetGroup}
                    />
                  )}
                </div>

                <div className="col-span-12 md:col-span-6 lg:col-span-4 form-group form-group-lg">
                  <label htmlFor="group">{t('maintenanceGroupLabel')}</label>
                  <Select
                    id="group"
                    placeholder={t('maintenanceGroupPlaceholder')}
                    options={[
                      { id: "1", label: t('groupRoadway') },
                      { id: "2", label: t('groupMixed') },
                      { id: "3", label: t('groupSevere') },
                      { id: "4", label: t('groupSpecial') },
                    ].map((group: any) => ({
                      id: group.id,
                      label: group.label,
                    }))}
                    value={
                      form?.assetsAndDrivers?.asset?.group
                        ? [form?.assetsAndDrivers?.asset?.group]
                        : []
                    }
                    useFilter
                    noItemMessage={t('noGroupFound')}
                    onChange={async (e) => {
                      setLoading(true);
                      const checkup = await getCheckupByMetric(
                        form?.assetsAndDrivers?.asset?.odometer ?? 0,
                        form?.assetsAndDrivers?.asset?.hourmeter ?? 0,
                        form?.assetsAndDrivers?.asset?.chassis ?? "",
                        e?.id ?? "",
                        form?.assetsAndDrivers?.asset?.modelCode ?? ""
                      );
                      setLoading(false);
                      setForm({
                        ...form,
                        assetsAndDrivers: {
                          ...form?.assetsAndDrivers,
                          checkup,
                          asset: form?.assetsAndDrivers?.asset
                            ? {
                              ...form?.assetsAndDrivers?.asset,
                              group: e?.id ?? "",
                            }
                            : undefined,
                        },
                      });
                    }}
                    disabled={disableAssetGroup}
                  />
                </div>

                <div className="col-span-12 md:col-span-6 lg:col-span-4 form-group form-group-lg">
                  <label htmlFor="emissionStandard">{t('legislationLabel')}</label>
                  {form?.assetsAndDrivers?.asset?.emissionStandard ? (
                    <input
                      id="emissionStandard"
                      className="form-control"
                      type="text"
                      value={form?.assetsAndDrivers?.asset?.emissionStandard}
                      disabled
                    />
                  ) : (
                    <Select
                      id="emissionStandard"
                      className="w-full emission-select"
                      placeholder={t('legislationPlaceholder')}
                      options={[
                        { id: "EURO 3", label: "EURO 3" },
                        { id: "EURO 5", label: "EURO 5" },
                        { id: "EURO 6", label: "EURO 6" },
                        { id: "ELETRICO", label: "ELÉTRICO" },
                      ]}
                      value={form?.assetsAndDrivers?.asset?.emissionStandard ? [form?.assetsAndDrivers?.asset?.emissionStandard] : []}
                      onChange={(option: any) => {
                        setForm({
                          ...form,
                          assetsAndDrivers: {
                            ...form?.assetsAndDrivers,
                            asset: form?.assetsAndDrivers?.asset ? { ...form.assetsAndDrivers.asset, emissionStandard: option?.id } : undefined,
                          },
                        });
                      }}
                      useFilter
                      noItemMessage={t('noLegislationFound')}
                      disabled={!!appointmentId}
                    />
                  )}
                </div>

                <div className="col-span-12 md:col-span-6 lg:col-span-4 form-group form-group-lg">
                  <label htmlFor="odometro">{t('odometerLabel')}</label>
                  <div className="input-group">
                    <input
                      id="odometro"
                      className="form-control"
                      type="text"
                      placeholder={t('odometerPlaceholder')}
                      value={form?.assetsAndDrivers?.asset?.odometer}
                      disabled={loading || !!appointmentId}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        setForm({
                          ...form,
                          assetsAndDrivers: {
                            ...form?.assetsAndDrivers,
                            asset: form?.assetsAndDrivers?.asset
                              ? {
                                ...form?.assetsAndDrivers?.asset,
                                odometer: value,
                                group: form?.assetsAndDrivers?.asset?.group,
                              }
                              : undefined,
                          },
                        });
                        // Executa o cálculo com debounce
                        debouncedOdometerCheckup(value, form?.assetsAndDrivers?.asset);
                      }}
                      onBlur={async (e) => {
                        if (e.target.value) {
                          await setCheckup(
                            Number(e.target.value),
                            "odometer",
                            form?.assetsAndDrivers?.asset
                          );
                        }
                      }}
                    />
                  </div>
                </div>

                <div className="col-span-12 md:col-span-6 lg:col-span-4 form-group form-group-lg">
                  <label htmlFor="horimetro">{t('hourmeterLabel')}</label>
                  <input
                    id="horimetro"
                    className="form-control"
                    type="text"
                    placeholder={t('hourmeterPlaceholder')}
                    disabled={loading || !!appointmentId}
                    value={form?.assetsAndDrivers?.asset?.hourmeter}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      setForm({
                        ...form,
                        assetsAndDrivers: {
                          ...form?.assetsAndDrivers,
                          asset: form?.assetsAndDrivers?.asset
                            ? {
                              ...form?.assetsAndDrivers?.asset,
                              hourmeter: value,
                              group: form?.assetsAndDrivers?.asset?.group,
                            }
                            : undefined,
                        },
                      });
                      // Executa o cálculo com debounce
                      debouncedHourmeterCheckup(value, form?.assetsAndDrivers?.asset);
                    }}
                    onBlur={async (e) => {
                      if (e.target.value) {
                        await setCheckup(
                          Number(e.target.value),
                          "hourmeter",
                          form?.assetsAndDrivers?.asset
                        );
                      }
                    }}
                  />
                </div>

                <div className="col-span-12 md:col-span-6 lg:col-span-4 form-group form-group-lg">
                  <label htmlFor="cnh">{t('driverLicenseLabel')}</label>
                  <div className="input-group">
                    <input
                      id="cnh"
                      className="form-control"
                      type="text"
                      placeholder="00000000000"
                      maxLength={12}
                      disabled={loading || !!appointmentId}
                      value={form?.assetsAndDrivers?.driver?.license}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          assetsAndDrivers: {
                            ...form?.assetsAndDrivers,
                            driver: form?.assetsAndDrivers?.driver
                              ? {
                                ...form?.assetsAndDrivers?.driver,
                                license: e.target.value,
                              }
                              : undefined,
                          },
                        })
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Mensagem de erro para veículos com agendamento existente */}
            {vehicleError && (
              <div className="col-span-12">
                <div className="mt-3 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-900 rounded-md">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-6 w-6 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-red-700 dark:text-red-300" style={{ fontSize: '16px' }}>
                        {vehicleError}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="border rounded padding-15 margin-top-20 scheduling-page__fieldset">
              <h6 className="text-bold text-size-16">
                {t('revisionsSection')}
              </h6>
              {form?.assetsAndDrivers?.asset?.model && form?.assetsAndDrivers?.asset?.model !== "-" && form?.assetsAndDrivers?.asset?.group && form?.assetsAndDrivers?.asset?.group !== "-" ? (
                <>
                  {!appointmentId ? (
                    <div className="info-group">
                      <p className="text-color-status-resting text-size-14 padding-top-10 padding-bottom-5">
                        {t('availableRevisions')}
                        <span className="asterisk-color">*</span>
                        <span className="rioglyph rioglyph-question question-icon"></span>
                      </p>
                      <div className="display-flex gap-10">
                        {[
                          { id: "preventive", label: t('preventive') },
                          { id: "corrective", label: t('corrective') },
                          { id: "workshopActions", label: t('workshopActions') },
                        ].map((option) => {
                          if (
                            option.id === "preventive" &&
                            (!form?.assetsAndDrivers?.checkup || form?.assetsAndDrivers?.checkup?.parts?.length === 0)
                          )
                            return null;
                          if (
                            option.id === "workshopActions" &&
                            !form?.assetsAndDrivers?.asset?.campaigns?.list?.length
                          )
                            return null;
                          return (
                            <div className="avalilable-revisions border rounded mb-2 lg:mb-0">
                              <Checkbox
                                onChange={() => {
                                  let schedulingTypes = form?.schedulingTypes;

                                  schedulingTypes = schedulingTypes?.includes(
                                    option.id
                                  )
                                    ? schedulingTypes?.filter(
                                      (type) => type !== option.id
                                    )
                                    : [...(schedulingTypes ?? []), option.id];

                                  setForm({
                                    ...form,
                                    schedulingTypes,
                                  });
                                }}
                                checked={form?.schedulingTypes?.includes(option.id)}
                                custom
                                className="padding-y-5 padding-x-10"
                              >
                                <div className="checkbox-text-wrapper display-flex gap-10">
                                  <div className="checkbox-text" />
                                  <div className="flex-1-1">{option.label}</div>
                                </div>
                              </Checkbox>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </>) :
                <div className="text-color-status-resting text-size-16 padding-top-10 padding-bottom-5">
                  {t('noRevisionsHint')}
                </div>
              }

              {form?.assetsAndDrivers?.asset?.chassis !== "-" &&
                form?.schedulingTypes?.length ? (
                <div
                  style={{
                    backgroundColor: isDarkMode ? "#0f2e21" : "#ECFDF3",
                    color: isDarkMode ? "#4ade80" : "#067647",
                    borderColor: isDarkMode ? "#166534" : "#ABEFC6",
                  }}
                  className="border rounded padding-15 margin-top-20"
                >
                  {form?.schedulingTypes?.includes("preventive") &&
                    form?.assetsAndDrivers?.checkup && (
                      <div className="margin-bottom-20">
                        <div className="text-bold">
                          {form?.assetsAndDrivers?.checkup.description}
                        </div>
                        {(form?.assetsAndDrivers?.checkup.services ?? []).map(
                          (service: CheckupService) => (
                            <div className="padding-left-10 padding-top-10">
                              {service.name}
                            </div>
                          )
                        )}
                        <div className="text-bold padding-top-10">
                          {form?.assetsAndDrivers?.checkup?.parts?.length ? t('partsToReplace') : t('noPartsToReplace')}
                        </div>
                        <div className="display-grid grid-cols-2 gap-x-20 gap-y-5 padding-top-10 padding-left-10">
                          {(form?.assetsAndDrivers?.checkup?.parts ?? []).map(
                            (part) => (
                              <div key={part.code} className="display-flex flex-column gap-5">
                                <div className="display-flex align-items-center justify-content-between gap-10">
                                  <span className="flex-1 min-w-0">{part.qtd}x - {part.description} - {part.code}</span>
                                  <span className={`flex-shrink-0 px-2 py-1 rounded text-size-12 font-medium uppercase whitespace-nowrap text-white ${part.isCovered !== false ? "green-tag" : "bg-red-600"}`} title={part.isCovered === false && part.notCoveredReason ? part.notCoveredReason : undefined}>
                                    {part.isCovered !== false ? t('coveredByPlan') : t('noCoverage')}
                                  </span>
                                </div>
                                {part.isCovered === false && part.notCoveredReason?.trim() && (
                                  <div className="text-size-12 text-color-status-resting padding-left-10">
                                    {part.notCoveredReason}
                                  </div>
                                )}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}

                  {form?.schedulingTypes?.includes("workshopActions") && (
                    <>
                      <div className="text-bold">{t('fieldCampaigns')}</div>
                      {(
                        form?.assetsAndDrivers?.asset?.campaigns?.list ?? []
                      ).map((campaign: CampaignsDetails) => (
                        <div className="padding-left-10 padding-top-10">
                          {campaign.name}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              ) : null}
            </div>

            <div className="border rounded padding-15 margin-top-20 scheduling-page__fieldset">
              <div className="flex justify-between">
                <h6 className="text-bold text-size-16">{t('generalObservations')}</h6>
              </div>

              <div className="info-group">
                <div className="add-services">
                  <div className="text-comments margin-top-10">
                    <textarea
                      style={{ height: "100px" }}
                      className="form-control"
                      placeholder={t('observationsPlaceholder')}
                      value={form?.assetsAndDrivers?.obs}
                      disabled={!!appointmentId}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          assetsAndDrivers: {
                            ...form?.assetsAndDrivers,
                            obs: e.target.value,
                          },
                        })
                      }
                    ></textarea>
                  </div>
                  {form?.schedulingTypes?.includes("corrective") && (form?.assetsAndDrivers?.obs?.trim() ?? "").length > 0 && (
                    <div className="margin-top-15 padding-15 rounded bg-green-100 dark:bg-green-950 border border-green-200 dark:border-green-900 text-color-darker text-size-14">
                      {t('coverageCheckInfo')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="w-full lg:w-1/3 md:w-full calendar-col">
            <h3 className="text-center">{t('calendar')}</h3>

            <div className="mb-4">
              <div className="calendar-row margin-top-10">
                <div className="flex items-center justify-between mb-2 calendar-row__month">
                  <button onClick={previousWeek} className="calendar-nav-btn">
                    <span className="rioglyph rioglyph-chevron-left text-color-status-available"></span>
                  </button>
                  <span className="text-size-16 text-color-status-resting font-semibold capitalize">
                    {firstWeekDay
                      .toLocaleDateString("pt-BR", {
                        month: "long",
                        year: "numeric",
                      })
                      .replace(" de ", "/")}
                  </span>
                  <button onClick={nextWeek} className="calendar-nav-btn">
                    <span className="rioglyph rioglyph-chevron-right text-color-status-available"></span>
                  </button>
                </div>
                <div className="flex justify-between text-center p-2 calendar-row__days overflow-x-auto">
                  {(weekDays ?? []).map((weekDay: any) => {
                    const isSelected = (() => {
                      return choosedDate?.getDay() ===
                        weekDay.date.getDay() &&
                        choosedDate?.getMonth() ===
                        weekDay.date.getMonth() &&
                        choosedDate?.getFullYear() ===
                        weekDay.date.getFullYear();
                    })();

                    return (
                      <button
                        key={`${weekDay.weekDay}_${weekDay.day}`}
                        className={`
                          flex flex-col items-center gap-5 p-2 w-12 h-16 rounded-lg text-sm transition-all 
                          ${isSelected ? "active-date text-white" : "hover:bg-gray-200 dark:hover:bg-neutral-700"}
                        `}
                        onClick={() => [
                          setChoosedDate(weekDay.date),
                          getConsultantAvailability(weekDay.date),
                        ]}
                      >
                        <span className="text-size-12 text-gray-600 dark:text-neutral-400 text-lowercase">
                          {weekDay.weekDay}
                        </span>
                        <span 
                          className={`font-medium flex items-center justify-center text-size-14 day-card bg-white dark:bg-neutral-700 dark:text-neutral-200`}>
                          {weekDay.day}
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>

              {choosedDate && (
                <div className="select-date-row">
                  <h5 className="text-center">{t('selectTime')}</h5>
                  <div className="hour-slots flex flex-col">
                    {(() => {
                      if (!choosedDate) return null;

                      let timeSlots: { hour: number; minute: number }[] = [];
                      let startHour = 0;
                      const endHour = 18; // Limitado até 18:00

                      for (let hour = startHour; hour <= endHour; hour++) {
                        timeSlots.push({ hour, minute: 0 });
                        if (hour !== endHour) {
                          timeSlots.push({ hour, minute: 30 });
                        }
                      }

                      return timeSlots.length === 0 ? (
                        <p className="text-center text-gray-500 dark:text-neutral-400">
                          {t('unavailable')}
                        </p>
                      ) : (
                        timeSlots.map(({ hour, minute }) => {
                          const slotDate = new Date(choosedDate);
                          slotDate.setHours(hour, minute, 0, 0);

                          const isUnavailable = consultantScheduleDates
                            .map((scheduleDate) =>
                              scheduleDate.toLocaleString("pt-Br")
                            )
                            .includes(slotDate.toLocaleString("pt-Br"));

                          const isSelected =
                            form?.dateAndConsultant?.scheduleDate?.getTime() ===
                            slotDate.getTime();

                          return (
                            <div
                              key={`${hour}-${minute}`}
                              className={`hour-row flex gap-3 lg:gap-6 md:gap-4 ${isSelected ? "active" : ""
                                }`}
                              onClick={() => {
                                if (!isUnavailable)
                                  selectDate(new Date(slotDate));
                              }}
                            >
                              <span className="hour-slot">
                                {`${hour.toString().padStart(2, "0")}:${minute
                                  .toString()
                                  .padStart(2, "0")}`}
                              </span>
                              <span
                                className={`hour-select ${isUnavailable ? "bg-lighter" : ""
                                  }`}
                              >
                                {isUnavailable
                                  ? t('unavailableTime')
                                  : isSelected
                                    ? t('selected')
                                    : t('select')}
                              </span>
                            </div>
                          );
                        })
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="fixed-footer">
            <div className="page-info flex justify-center items-center mb-1">
              <span className="text-color-status-resting text-size-16 text-center w-full">
                {t('footerInfo')}
              </span>
            </div>
            <div className="navigation-buttons step-1 flex flex-row gap-2 w-full justify-between px-2">
              <button
                className="px-3 py-2 flex justify-items-start rounded-lg font-semibold button-color w-1/2"
                onClick={() => navigate("/control-tower/monitoring")}
              >
                <span className="rioglyph rioglyph-arrow-left text-size-16 mr-1"></span>
                {t('back')}
              </button>
              <button
                className="px-3 py-2 btn btn-primary w-auto justify-items-start"
                disabled={!formIsValid()}
                onClick={goToSummary}
              >
                {t('finishAppointment')}
              </button>
            </div>
          </div>
        </div>
      </div>
      <Spinner show={loading} text={t('searchingRevision')} isFullSized />
      <Dialog
        show={showDialog}
        onClose={handleClose}
        showCloseButton
        bsSize={Dialog.SIZE_SM}
      >
        <Dialog.Title title={t('leaveDialogTitle')} />
        <Dialog.Body>
          <div className="cancel-content flex flex-col">
            <span className="text-color-darker text-size-16 text-normal">
              {t('leaveDialogBody')}
              <br />
              <br />
              {t('leaveDialogQuestion')}
            </span>
          </div>
        </Dialog.Body>
        <Dialog.Footer>
          <div className="flex items-center justify-end">
            <div className="flex items-center gap-6">
              <span
                className="text-medium text-size-14 cursor-pointer text-color-primary"
                onClick={handleClose}
              >
                {t('leaveDialogBack')}
              </span>

              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setShowDialog(false);
                  window.history.back();
                }}
              >
                {t('leaveDialogContinue')}
              </button>
            </div>
          </div>
        </Dialog.Footer>
      </Dialog>
      <ActiveAppointmentDialog
        show={activeAppointmentDialog.show}
        chassis={activeAppointmentDialog.chassis}
        appointment={activeAppointmentDialog.appointment}
        onClose={() => setActiveAppointmentDialog({ show: false })}
      />
    </>
  );
};

export default AppointmentForm;
